import { apiClient } from '../lib/apiClient';

/**
 * FieldEditForm — bottom sheet for editing field attributes.
 * Pre-populated with current field data, calls PATCH /api/v1/aif/farm/fields/{id}.
 *
 * Props:
 *   field    — field object with current data
 *   token    — JWT
 *   onSave   — (updatedField) => void — called after successful save
 *   onCancel — () => void
 */
import { useState, useId, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const SOIL_TYPE_KEYS = [
  { value: '', i18nKey: 'field.soilTypeSelect' },
  { value: 'loam',        i18nKey: 'field.soilLoam' },
  { value: 'sandy_loam',  i18nKey: 'field.soilSandyLoam' },
  { value: 'clay_loam',   i18nKey: 'field.soilClayLoam' },
  { value: 'clay',        i18nKey: 'field.soilClay' },
  { value: 'sand',        i18nKey: 'field.soilSand' },
  { value: 'silt',        i18nKey: 'field.soilSilt' },
  { value: 'peat',        i18nKey: 'field.soilPeat' },
];

const CROP_KEYS = [
  { value: '', i18nKey: 'field.cropNone' },
  { value: 'wheat',     i18nKey: 'field.cropWheat' },
  { value: 'barley',    i18nKey: 'field.cropBarley' },
  { value: 'rapeseed',  i18nKey: 'field.cropRapeseed' },
  { value: 'corn',      i18nKey: 'field.cropCorn' },
  { value: 'potato',    i18nKey: 'field.cropPotato' },
  { value: 'sugarbeet', i18nKey: 'field.cropSugarbeet' },
  { value: 'grassland', i18nKey: 'field.cropGrassland' },
  { value: 'sunflower', i18nKey: 'field.cropSunflower' },
  { value: 'peas',      i18nKey: 'field.cropPeas' },
  { value: 'fababean',  i18nKey: 'field.cropFababean' },
  { value: 'other',     i18nKey: 'field.cropOther' },
];

export default function FieldEditForm({ field, token, onSave, onCancel }) {
  const { t } = useTranslation();
  const titleId = useId();
  const firstInputRef = useRef(null);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  // Escape key closes form
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  const [name, setName] = useState(field.name || '');
  const [crop, setCrop] = useState(field.current_crop || '');
  const [soilType, setSoilType] = useState(field.soil_type || '');
  const [soilQualityIndex, setSoilQualityIndex] = useState(
    field.soil_quality_index != null ? String(field.soil_quality_index) : ''
  );
  const [nitrateZone, setNitrateZone] = useState(field.nitrate_zone || false);
  const [organic, setOrganic] = useState(field.organic || field.organic_field || false);
  const [pachtRate, setPachtRate] = useState(
    field.pacht_rate_eur_ha != null ? String(field.pacht_rate_eur_ha) : ''
  );
  const [notes, setNotes] = useState(field.notes || field.attributes?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!name.trim()) { setError(t('field.nameRequired')); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim() };
      body.current_crop = crop || null;
      body.soil_type = soilType || null;
      body.soil_quality_index = soilQualityIndex ? parseInt(soilQualityIndex, 10) : null;
      body.nitrate_zone = nitrateZone;
      body.organic_field = organic;
      body.pacht_rate_eur_ha = pachtRate ? parseFloat(pachtRate) : null;
      body.notes = notes.trim() || null;

      const updated = await apiClient.patch(`/api/v1/aif/farm/fields/${field.id}`, body, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      onSave?.(updated);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                    bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                    focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]`;

  return (
    <div
      data-testid="field-edit-form"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="absolute bottom-0 left-0 right-0 z-[var(--aif-z-bottom-sheet)] bg-[var(--aif-bg-surface)] rounded-t-2xl
                 shadow-2xl border-t border-[var(--aif-border-default)] max-h-[80vh] overflow-y-auto"
    >
      {/* Drag handle */}
      <div className="flex justify-center pt-2 pb-1 sticky top-0 bg-[var(--aif-bg-surface)]" aria-hidden="true">
        <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
      </div>

      <div className="px-4 pb-6 space-y-4">
        <h2 id={titleId} className="text-base font-semibold text-[var(--aif-text-primary)]">
          {t('form.editField')}
        </h2>

        {error && (
          <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('form.fieldName')} *</label>
          <input
            ref={firstInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('field.namePlaceholder')}
            className={inputCls}
          />
        </div>

        {/* Crop */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('form.currentCrop')}</label>
          <select value={crop} onChange={(e) => setCrop(e.target.value)} className={inputCls}>
            {CROP_KEYS.map(c => <option key={c.value} value={c.value}>{t(c.i18nKey)}</option>)}
          </select>
        </div>

        {/* Soil type */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('form.soilType')}</label>
          <select value={soilType} onChange={(e) => setSoilType(e.target.value)} className={inputCls}>
            {SOIL_TYPE_KEYS.map(s => <option key={s.value} value={s.value}>{t(s.i18nKey)}</option>)}
          </select>
        </div>

        {/* Soil quality */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.soilQualityRange')}</label>
          <input
            type="number"
            min="1"
            max="100"
            value={soilQualityIndex}
            onChange={(e) => setSoilQualityIndex(e.target.value)}
            placeholder={t('field.soilQualityPlaceholder')}
            className={inputCls}
          />
        </div>

        {/* Flags */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[var(--aif-text-primary)] cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={nitrateZone}
              onChange={(e) => setNitrateZone(e.target.checked)}
              className="rounded"
            />
            {t('field.nitrateZone')}
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--aif-text-primary)] cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={organic}
              onChange={(e) => setOrganic(e.target.checked)}
              className="rounded"
            />
            {t('field.organicFarm')}
          </label>
        </div>

        {/* Pacht */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.leaseRateOptional')}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={pachtRate}
            onChange={(e) => setPachtRate(e.target.value)}
            placeholder={t('field.leaseRatePlaceholder')}
            className={inputCls}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.notesOptional')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('field.notesPlaceholder')}
            rows={2}
            className={`${inputCls} resize-none`}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       text-[var(--aif-text-secondary)] text-sm font-medium
                       hover:bg-[var(--aif-bg-elevated)] transition disabled:opacity-50"
          >
            {t('form.cancel')}
          </button>
          <button
            data-testid="field-edit-save"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                       hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? t('form.saving') : t('field.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}
